import i18next from 'i18next';

import en from './i18n/en';
import es from './i18n/es';

i18next.addResourceBundle('en', 'OccupationComparison', en);
i18next.addResourceBundle('es', 'OccupationComparison', es);

export type OccupationComparisonTranslateFn = (key: string, options?: Record<string, string | number>) => string;

export enum OccupationComparisonType {
  SKILL_MATCH = 'skillMatch',
  GROWTH = 'growth',
  SALARY = 'salary',
  AI_IMPACT = 'aiImpact',
  OCCUPATION_DEMAND = 'occupationDemand',
}

export enum OccupationComparisonTone {
  GREEN = 'green',
  ORANGE = 'orange',
  RED = 'red',
  DISABLED = 'disabled',
}

export interface IOccupationComparisonFilter {
  value: OccupationComparisonType;
  labelKey: string;
  selectedLabelKey?: string;
  suffix: string;
  max: number;
  yAxisLabels: string[];
}

export interface IOccupationComparisonMetric {
  value?: number;
  label: string;
  tone: OccupationComparisonTone;
}

export interface IOccupationComparisonOccupation {
  id: string;
  name: string;
  /** Skill match % from learner profile (useObjectives / calculateJobPreferenceMatch). */
  match?: number;
  growth?: number;
  salary?: number;
  aiImpact?: number;
  occupationDemand?: number;
}

export const OCCUPATION_COMPARISON_FILTERS: IOccupationComparisonFilter[] = [
  {
    value: OccupationComparisonType.SKILL_MATCH,
    labelKey: 'SKILL_MATCH',
    suffix: '%',
    max: 100,
    yAxisLabels: ['100%', '80%', '60%', '40%', '20%', '0'],
  },
  {
    value: OccupationComparisonType.GROWTH,
    labelKey: 'GROWTH',
    selectedLabelKey: 'GROWTH_LAST_7_DAYS',
    suffix: '%',
    max: 100,
    yAxisLabels: ['100%', '80%', '60%', '40%', '20%', '0'],
  },
  {
    value: OccupationComparisonType.SALARY,
    labelKey: 'SALARY',
    suffix: 'k',
    max: 100,
    yAxisLabels: ['100k', '75k', '50k', '25k', '0'],
  },
  {
    value: OccupationComparisonType.AI_IMPACT,
    labelKey: 'AI_IMPACT',
    suffix: '',
    max: 5,
    yAxisLabels: ['Very High', 'High', 'Moderate', 'Very Low', 'Low'],
  },
  {
    value: OccupationComparisonType.OCCUPATION_DEMAND,
    labelKey: 'OCCUPATION_DEMAND',
    suffix: '',
    max: 5,
    yAxisLabels: ['5', '4', '3', '2', '1', '0'],
  },
];

export interface IOccupationComparisonBackendMetrics {
  salary?: number;
  aiImpact?: number;
  occupationDemand?: number;
}

export const mergeOccupationComparisonMetrics = (
  occupations: IOccupationComparisonOccupation[],
  filtersByJobTitleId: Map<string, IOccupationComparisonBackendMetrics>,
  growthByJobTitleId: Map<string, number | undefined>,
): IOccupationComparisonOccupation[] =>
  occupations.map((occupation) => {
    const filters = filtersByJobTitleId.get(occupation.id);

    return {
      ...occupation,
      growth: growthByJobTitleId.get(occupation.id),
      salary: filters?.salary,
      aiImpact: filters?.aiImpact,
      occupationDemand: filters?.occupationDemand,
    };
  });

const getPositiveTone = (value: number, greenThreshold: number, orangeThreshold: number): OccupationComparisonTone => {
  if (value >= greenThreshold) {
    return OccupationComparisonTone.GREEN;
  }

  if (value >= orangeThreshold) {
    return OccupationComparisonTone.ORANGE;
  }

  return OccupationComparisonTone.RED;
};

const getLowerIsBetterTone = (
  value: number,
  greenThreshold: number,
  orangeThreshold: number,
): OccupationComparisonTone => {
  if (value <= greenThreshold) {
    return OccupationComparisonTone.GREEN;
  }

  if (value <= orangeThreshold) {
    return OccupationComparisonTone.ORANGE;
  }

  return OccupationComparisonTone.RED;
};

const getToneByType = (type: OccupationComparisonType, value?: number): OccupationComparisonTone => {
  if (typeof value !== 'number') {
    return OccupationComparisonTone.DISABLED;
  }

  switch (type) {
    case OccupationComparisonType.SKILL_MATCH:
      return getPositiveTone(value, 70, 40);

    case OccupationComparisonType.GROWTH:
      return getPositiveTone(value, 15, 5);

    case OccupationComparisonType.SALARY:
      return getPositiveTone(value, 55000, 40000);

    case OccupationComparisonType.AI_IMPACT:
      return getLowerIsBetterTone(value, 2, 3);

    case OccupationComparisonType.OCCUPATION_DEMAND:
      return getPositiveTone(value, 4, 2);

    default:
      return OccupationComparisonTone.DISABLED;
  }
};

const formatValue = (
  type: OccupationComparisonType,
  value: number | undefined,
  t: OccupationComparisonTranslateFn,
): string => {
  if (typeof value !== 'number') {
    return '-';
  }

  switch (type) {
    case OccupationComparisonType.SKILL_MATCH:
      return t('METRIC_SKILL_MATCH', { value: Math.round(value) });

    case OccupationComparisonType.GROWTH:
      return t('METRIC_GROWTH', { value: Math.round(value) });

    case OccupationComparisonType.SALARY:
      return t('METRIC_SALARY_PER_YEAR', { value: Math.round(value / 1000) });

    case OccupationComparisonType.AI_IMPACT: {
      const level = Math.round(value);

      if (level >= 1 && level <= 5) {
        return t(`AI_IMPACT_LEVEL_${level}`);
      }

      return t('METRIC_AI_LEVEL_FALLBACK', { value: level });
    }

    case OccupationComparisonType.OCCUPATION_DEMAND: {
      const level = Math.round(value);

      if (level >= 1 && level <= 5) {
        return t(`DEMAND_LEVEL_${level}`);
      }

      return t('METRIC_DEMAND_LEVEL_FALLBACK', { value: level });
    }

    default:
      return `${value}`;
  }
};

const getMetricValueByType = (
  type: OccupationComparisonType,
  occupation: IOccupationComparisonOccupation,
): number | undefined => {
  switch (type) {
    case OccupationComparisonType.SKILL_MATCH:
      return typeof occupation.match === 'number' ? occupation.match : undefined;

    case OccupationComparisonType.GROWTH:
      return typeof occupation.growth === 'number' ? occupation.growth : undefined;

    case OccupationComparisonType.SALARY:
      return typeof occupation.salary === 'number' ? occupation.salary : undefined;

    case OccupationComparisonType.AI_IMPACT:
      return typeof occupation.aiImpact === 'number' ? occupation.aiImpact : undefined;

    case OccupationComparisonType.OCCUPATION_DEMAND:
      return typeof occupation.occupationDemand === 'number' ? occupation.occupationDemand : undefined;

    default:
      return undefined;
  }
};

export const getOccupationComparisonMetric = (
  type: OccupationComparisonType,
  occupation: IOccupationComparisonOccupation,
  t: OccupationComparisonTranslateFn,
): IOccupationComparisonMetric => {
  const value = getMetricValueByType(type, occupation);

  return {
    value,
    label: formatValue(type, value, t),
    tone: getToneByType(type, value),
  };
};

export const getOccupationComparisonFilter = (type: OccupationComparisonType): IOccupationComparisonFilter =>
  OCCUPATION_COMPARISON_FILTERS.find((filter) => filter.value === type) ?? OCCUPATION_COMPARISON_FILTERS[0];
